import React, { useEffect, useState } from 'react';
import { UserPlus, Trash2, Edit, MoreVertical, Search, Mail, Shield, Users, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700', description: 'Full access to all features' },
  editor: { label: 'Editor', color: 'bg-blue-100 text-blue-700', description: 'Can create and edit content' },
  viewer: { label: 'Viewer', color: 'bg-slate-100 text-slate-700', description: 'Read-only access' },
};

const Team = () => {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Invite dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteData, setInviteData] = useState({ full_name: '', email: '', password: '', role: 'editor' });
  const [inviting, setInviting] = useState(false);
  
  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editData, setEditData] = useState({ full_name: '', email: '', role: '' });
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingMember, setDeletingMember] = useState(null);
  
  // Reset password dialog
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resetPasswordMember, setResetPasswordMember] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await api.get('/team');
      setTeam(response.data);
    } catch (error) {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteData.full_name || !inviteData.email || !inviteData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    setInviting(true);
    try {
      const response = await api.post('/team/invite', inviteData);
      toast.success(`${inviteData.full_name} has been added to your team`);
      setShowInviteDialog(false);
      setInviteData({ full_name: '', email: '', password: '', role: 'editor' });
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    } finally {
      setInviting(false);
    }
  };

  const handleEditMember = async () => {
    if (!editingMember) return;
    
    try {
      const updatePayload = {};
      if (editData.full_name && editData.full_name !== editingMember.full_name) {
        updatePayload.full_name = editData.full_name;
      }
      if (editData.email && editData.email !== editingMember.email) {
        updatePayload.email = editData.email;
      }
      if (editData.role && editData.role !== editingMember.role) {
        updatePayload.role = editData.role;
      }
      
      if (Object.keys(updatePayload).length === 0) {
        toast.info('No changes to save');
        setShowEditDialog(false);
        return;
      }
      
      await api.put(`/team/${editingMember.user_id}`, updatePayload);
      toast.success(`Updated ${editData.full_name}'s details`);
      setShowEditDialog(false);
      setEditingMember(null);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleDelete = async () => {
    if (!deletingMember) return;

    try {
      await api.delete(`/team/${deletingMember.user_id}`);
      toast.success(`${deletingMember.full_name} has been removed from the team`);
      setShowDeleteDialog(false);
      setDeletingMember(null);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setEditData({ 
      full_name: member.full_name || '', 
      email: member.email || '', 
      role: member.role || 'editor' 
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (member) => {
    setDeletingMember(member);
    setShowDeleteDialog(true);
  };

  const openResetPasswordDialog = (member) => {
    setResetPasswordMember(member);
    setNewPassword('');
    setShowResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordMember || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      await api.post(`/team/${resetPasswordMember.user_id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset successfully for ${resetPasswordMember.full_name}`);
      setShowResetPasswordDialog(false);
      setResetPasswordMember(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredTeam = team.filter(member => 
    member.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    member.email?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="flex" data-testid="team-page">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Team Members</h1>
              <p className="text-slate-500 mt-1">Manage staff access to your account</p>
            </div>
            {isAdmin && (
              <Button
                onClick={() => setShowInviteDialog(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="invite-button"
              >
                <UserPlus size={18} className="mr-2" />
                Invite Team Member
              </Button>
            )}
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search team members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Team Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{team.length}</p>
                  <p className="text-xs text-slate-500">Total Members</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Shield size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{team.filter(m => m.role === 'admin').length}</p>
                  <p className="text-xs text-slate-500">Admins</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Edit size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{team.filter(m => m.role === 'editor').length}</p>
                  <p className="text-xs text-slate-500">Editors</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{team.filter(m => m.role === 'viewer').length}</p>
                  <p className="text-xs text-slate-500">Viewers</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Team List */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Loading team...</p>
            </div>
          ) : filteredTeam.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <Users size={32} className="text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {search ? 'No members found' : 'No team members yet'}
                </h3>
                <p className="text-slate-500 mb-4 text-center max-w-md">
                  {search ? 'Try adjusting your search' : 'Invite your first team member to collaborate on content'}
                </p>
                {!search && isAdmin && (
                  <Button onClick={() => setShowInviteDialog(true)}>
                    <UserPlus size={18} className="mr-2" /> Invite Team Member
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Member</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Role</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Joined</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Status</th>
                    {isAdmin && <th className="text-right px-6 py-3 text-sm font-medium text-slate-600">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredTeam.map((member) => (
                    <tr key={member.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold">
                            {member.full_name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{member.full_name}</p>
                            <p className="text-sm text-slate-500">{member.email}</p>
                          </div>
                          {member.user_id === user?.user_id && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={ROLE_CONFIG[member.role]?.color || 'bg-slate-100'}>
                          {ROLE_CONFIG[member.role]?.label || member.role}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {formatDate(member.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Active
                        </Badge>
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 text-right">
                          {member.user_id !== user?.user_id && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical size={16} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(member)}>
                                  <Edit size={16} className="mr-2" /> Edit Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openResetPasswordDialog(member)}>
                                  <KeyRound size={16} className="mr-2" /> Reset Password
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => openDeleteDialog(member)}
                                >
                                  <Trash2 size={16} className="mr-2" /> Remove
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Add a new staff member to your account. They will receive login credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={inviteData.full_name}
                onChange={(e) => setInviteData({ ...inviteData, full_name: e.target.value })}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                placeholder="john@company.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={inviteData.password}
                onChange={(e) => setInviteData({ ...inviteData, password: e.target.value })}
                placeholder="Create a password"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={inviteData.role} onValueChange={(v) => setInviteData({ ...inviteData, role: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {user?.role === 'super_admin' && (
                    <SelectItem value="super_admin">
                      <div className="flex flex-col">
                        <span className="font-medium">Super Admin</span>
                        <span className="text-xs text-slate-500">Platform-wide access, manage all tenants</span>
                      </div>
                    </SelectItem>
                  )}
                  <SelectItem value="admin">
                    <div className="flex flex-col">
                      <span className="font-medium">Admin</span>
                      <span className="text-xs text-slate-500">Full access to all features</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="editor">
                    <div className="flex flex-col">
                      <span className="font-medium">Editor</span>
                      <span className="text-xs text-slate-500">Can create and edit content</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex flex-col">
                      <span className="font-medium">Viewer</span>
                      <span className="text-xs text-slate-500">Read-only access</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting} className="bg-indigo-600 hover:bg-indigo-700">
              {inviting ? 'Adding...' : 'Add Team Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>
              Update details for {editingMember?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={editData.full_name}
                onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                placeholder="Full name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                placeholder="Email address"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editData.role} onValueChange={(v) => setEditData({ ...editData, role: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {user?.role === 'super_admin' && (
                    <SelectItem value="super_admin">Super Admin - Platform-wide access</SelectItem>
                  )}
                  <SelectItem value="admin">Admin - Full access</SelectItem>
                  <SelectItem value="editor">Editor - Create & edit content</SelectItem>
                  <SelectItem value="viewer">Viewer - Read-only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditMember} className="bg-indigo-600 hover:bg-indigo-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {deletingMember?.full_name} from your team? 
              They will lose access to this account immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>
              Remove Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetPasswordMember?.full_name}. They will need to use this password on their next login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleResetPassword} 
              disabled={resettingPassword || !newPassword}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {resettingPassword ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Team;
